<template>
  <div>
    <o-loading :full-page="true" :active="isLoading" :can-cancel="false"></o-loading>
    <o-modal :active="isComponentModalActive" :canCancel="false" :class="folderPage ? 'modal-sm' : 'modal-xs'">
      <div class="modal-card is-small custom-popover">
        <header class="modal-card-head">
          <h3 class="modal-card-title">Select BIM 360 File</h3>
        </header>
        <section class="modal-card-body" :class="folderPage ? 'pl-0': 'is-visible'">
          <div v-show="!folderPage">
            <span class="is-size-3 has-text-weight-bold has-text-black-bis">
              Hub
            </span>
            <mf-multi-select
              v-model="selectedHub"
              :options="hubs"
              :closeOnSelect="true"
              label="name"
              track-by="name"
              :multiple="false"
              :hideSelected="false"
              :allow-empty="false"
              @update:modelValue="setHub($event)"
            ></mf-multi-select>
            <span class="is-size-3 has-text-weight-bold has-text-black-bis">
              Project
            </span>
            <mf-multi-select
              v-model="selectedProject"
              :options="hubProjects"
              :closeOnSelect="true"
              label="name"
              track-by="name"
              :multiple="false"
              :hideSelected="false"
              :allow-empty="false"
              @update:modelValue="getFolders($event)"
            ></mf-multi-select>
            <span class="is-size-3 has-text-weight-bold has-text-black-bis">
              Folder
            </span>
            <mf-multi-select
              v-model="selectedFolder"
              :options="mainFolders"
              :closeOnSelect="true"
              label="name"
              track-by="name"
              :multiple="false"
              :hideSelected="false"
              :allow-empty="false"
            ></mf-multi-select>
          </div>
          <div v-if="folderPage">
            <ul v-for="folder in subFolders.childrens" :key="folder.id">
              <FoldersList
                :folders="folder"
                :selectedProject="selectedProject"
                :project="project"
                :folderInfo="selectedFolder"
                @set-itemData="setItemData($event,)"
              ></FoldersList>
            </ul>
          </div>
        </section>
        <footer class="modal-card-foot">
          <div class="column is-paddingless">
            <button
              class="button is-outlined"
              type="button"
              @click="
              $emit('close-modal');"
            >Cancel</button>
          </div>
          <div class="column is-narrow is-paddingless">
            <button
              class="button has-background-black-bis has-text-white"
              type="button"
              v-if="!folderPage"
              :disabled="$_.isEmpty(selectedFolder)"
              @click="getSubFolder()"
            >Browse files...</button>
            <button
              class="button has-background-black-bis has-text-white"
              type="button"
              v-if="folderPage"
              @click="backPage()"
            >Back</button>
            <button
              class="button has-background-black-bis has-text-white"
              type="button"
              v-if="folderPage"
              @click="folderMethod()"
            >Ok</button>
          </div>
        </footer>
      </div>
    </o-modal>
  </div>
</template>

<script>
import {
  defineComponent,
  nextTick,
  onMounted,
  reactive,
  ref,
  toRef,
  toRefs,
} from 'vue';
import { useToast } from "vue-toastification";
import Forge from '@/models/Forge';
import FoldersList from '@/components/FoldersList.vue';
import _ from 'lodash';

export default defineComponent({
  name: 'BimFileAccess',
  components: { FoldersList },
  props: {
    isComponentModalActive: {
      type: Boolean,
      default: false,
    },
    project: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close-modal', 'set-urn'],
  setup(props, { emit }) {
    const toast = useToast();
    const data = reactive({
      hubs: [],
      hubProjects: [],
      mainFolders: [],
      selectedHub: {},
      selectedProject: {},
      selectedFolder: {},
      folderPage: false,
      subFolders: [],
      buttonDisable: true,
      item: {},
      folderInfo: {},
      isLoading: false,
    });

    const backPage = () => {
      data.folderPage = false;
      data.item = {};
    };

    const setItemData = (e) => {
      data.item = e.item;
      data.folderInfo = e.folderInfo;
    };

    const setHub = async (event) => {
      await getHubProjects(event.id);
    };

    const getHubs = async () => {
      try {
        data.isLoading = true;
        const hubsData = await Forge.getHubs();
        if (hubsData.length > 0) {
          data.hubs = _.filter(
            hubsData,
            (hub) => _.toString(hub.id).charAt(0) === 'b',
          ).map((hub) => ({
            id: hub.id,
            name: _.get(hub, 'attributes.name', ''),
          }));
          [data.selectedHub] = data.hubs;
          if (data.hubs.length > 0) {
            await getHubProjects(data.selectedHub.id);
          }
        }
      } catch (error) {
        toast.error(`Error: ${error.data.error}`);
        emit('close-modal');
      } finally {
        data.isLoading = false;
      }
    };

    const getHubProjects = async (id) => {
      const hubProjectsData = await Forge.getHubsProjects(id);
      data.hubProjects = _.map(
        hubProjectsData.data,
        (project) => ({ id: project.id, name: project.attributes.name }),
      );
      if (_.isEmpty(hubProjectsData.data)) {
        nextTick(() => {
          data.selectedProject = [];
          getFolders();
        });
      } else {
        nextTick(() => {
          [data.selectedProject] = data.hubProjects;
          getFolders();
        });
      }
    };

    const getFolders = async () => {
      data.isLoading = true;
      if (_.isEmpty(data.selectedProject)) {
        nextTick(() => {
          data.selectedFolder = [];
        });
      } else {
        const params = {
          hubId: data.selectedHub.id,
          projectId: data.selectedProject.id,
        };
        const mainFoldersData = await Forge.getProjectFolders(params);
        data.mainFolders = _.filter(
          mainFoldersData,
          (hub) => hub.attributes.displayName !== 'Plans',
        ).map((project) => ({ id: project.id, name: project.attributes.name }));
        nextTick(() => {
          [data.selectedFolder] = data.mainFolders;
        });
      }
      data.isLoading = false;
    };

    const getSubFolder = async () => {
      try {
        data.isLoading = true;
        if (_.isEmpty(data.selectedFolder)) {
          data.subFolders = [];
          data.folderPage = false;
        } else {
          const params = {
            projectId: data.selectedProject.id,
            folderId: data.selectedFolder.id,
          };
          data.subFolders = await Forge.getFolderContents(params);
          _.map(data.subFolders, (folder) => {
            folder.childrens = [];
            folder.path = `${data.selectedFolder.name}`;
            return folder;
          });
          data.subFolders = {
            childrens: data.subFolders,
          };
          data.folderPage = true;
        }
      } catch (error) {
        confirm(`Error: ${error}. Please try again later.`);
        toast.error(`Error: ${error}`);
      } finally {
        data.isLoading = false;
      }
    };

    const folderMethod = async () => {
      if (_.isEmpty(data.item)) {
        toast.error('Please Select The File Then Click on OK');
        return;
      }
      const params = {
        itemId: data.item.id,
        itemProjectId: data.selectedProject.id,
      };
      try {
        const { id } = await Forge.getItemUrn(params);
        const forgeData = {
          path: `${data.item.path}/${data.item.attributes.displayName}`,
          id,
          itemId: data.item.id,
          folderId: data.folderInfo.id,
          projectId: data.selectedProject.id,
        };
        emit('set-urn', forgeData);
      } catch (e) {
        toast.error(`Error: ${e}`);
      }
    };

    onMounted(() => {
      getHubs();
    });

    return {
      ...toRefs(data),
      backPage,
      setItemData,
      setHub,
      getHubs,
      getHubProjects,
      getFolders,
      getSubFolder,
      folderMethod,
    };
  },
});
</script>

<style scoped>
div :deep(.overflow-auto) {
  overflow: auto !important;
}
</style>
